import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { object, string, ref, array } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { passwordSchemaTest } from "../../../../utils/password-utils";
import { useEffect, useState } from "react";
import { changeUser } from "../redux/usersSagas";

const schema = object({
  first_name: string().label("First Name").min(2).required(),
  last_name: string().label("Last Name").min(2).required(),
  email: string()
    .label("Email")
    .when({
      is: (value) => {
        return !!value;
      },
      then: (schema) => {
        return schema.email().required();
      }
    }),
  password: string()
    .label("Password")
    .when({ is: (value) => !!value, then: (schema) => schema.min(8).test(passwordSchemaTest) }),
  password_confirmation: string()
    .label("Password Confirmation")
    .when({
      is: (value) => !!value,
      then: (schema) => schema.oneOf([ref("password")], "${path} must match Password").min(8)
    }),
  role_id: object().required("Please choose role"),
  organization_ids: array().when("multi_organizations", {
    is: true,
    then: (schema) => {
      return schema.required().min(1, "Please Select at least 1 Organization");
    },
    otherwise: (schema) => {
      return schema.notRequired();
    }
  })
}).required();

export const useEdit = ({ isAdmin, id, orgName }) => {
  const dispatch = useDispatch();

  const [orgRoles, setOrgRoles] = useState([]);

  const currentUserInfo = useSelector((state) => state.users.currentUserInfo);
  const { filters: { roles, locations, organizations } = {}, permissions } = useSelector(
    (state) => state.users.commonSettings
  );

  const {
    firstName,
    lastName,
    email,
    roleId,
    phone,
    expiredAt,
    inactive,
    isCorporate,
    passwordChanged,
    tosAccepted,
    userOrganizationIds = [],
    corporateReporting,
    linkUsersToSharePermissions = [],
    enabled2fa,
    disabled2faByEmail,
    daysInterval2fa,
    isMultiOrganizations,
    organizationId,
    permissionIds = [],
    clubreadyStoreIds = [],
    corporateReportIds = [],
    sendSeparateDailyReports,
    sendInactiveUsersEmail,
    canAssignRole,
    availableForAssignRoleIds = []
  } = currentUserInfo || {};

  const selectedRole = roles.find((role) => role.id == roleId);
  const availableRoles = availableForAssignRoleIds.length
    ? roles.filter((role) => availableForAssignRoleIds.includes(role.id))
    : [];

  const defaultValues = {
    first_name: firstName,
    last_name: lastName,
    email,
    role_id: selectedRole ?? {},
    phone_number: phone,
    expires_at: expiredAt,
    inactive,
    password_changed: passwordChanged,
    tos_accepted: tosAccepted,

    enabled_2fa: enabled2fa,
    disabled_2fa_by_email: disabled2faByEmail,
    days_interval_2fa: daysInterval2fa,
    permission_ids: permissionIds.reduce((acc, val) => ({ ...acc, [val]: true }), {}),
    clubready_store_ids: clubreadyStoreIds.reduce((acc, val) => ({ ...acc, [val]: true }), {}),
    corporate_report_ids: corporateReportIds.reduce((acc, val) => ({ ...acc, [val]: true }), {}),
    send_separate_daily_reports: sendSeparateDailyReports,
    send_inactive_users_email: sendInactiveUsersEmail,
    password: "",
    password_confirmation: "",
    multi_organizations: isMultiOrganizations,
    organization_id: organizations.find((el) => el.id == organizationId) ?? {},
    organization_ids: userOrganizationIds.map((userOrgId) =>
      organizations.find((org) => org.id == userOrgId)
    ),
    can_assign_role: canAssignRole,
    available_for_assign_role_ids: availableRoles,
    corporate: isCorporate,
    corporate_reporting: corporateReporting,
    user_group_user_ids: linkUsersToSharePermissions.filter((user) => user.selected)
  };

  const form = useForm({
    resolver: yupResolver(schema),
    defaultValues: {},
    values: defaultValues
  });

  let {
    unregister,
    handleSubmit,
    setValue,
    watch,
    getValues,
    formState: { dirtyFields }
  } = form;

  const [currentOrg, isCorp, isMultiOrg, isCorporateReporting, canAssignRoleWatched, currentOrgs] =
    watch([
      "organization_id",
      "corporate",
      "multi_organizations",
      "corporate_reporting",
      "can_assign_role",
      "organization_ids"
    ]);

  const currentOrgId = currentOrg?.id;
  const canUpdateLocations = !(isMultiOrg || isCorp);
  const canSendInactiveUsersEmail = isMultiOrg || isCorp;

  const allPermissions = canUpdateLocations
    ? {
        location_permissions: locations.filter(
          (location) => location.organizationId === currentOrgId
        ),
        ...permissions
      }
    : permissions;

  const setLocations = () => {
    if (canUpdateLocations) {
      setValue(
        "clubready_store_ids",
        organizationId === currentOrgId ? defaultValues.clubready_store_ids : {},
        { shouldDirty: true }
      );
    }
  };

  const setCorporateReports = () => {
    if (isCorporateReporting) {
      setValue(
        "corporate_report_ids",
        organizationId === currentOrgId ? defaultValues.corporate_report_ids : {},
        { shouldDirty: true }
      );
    }
  };

  const setAssignRoles = () => {
    if (!isMultiOrg && canAssignRoleWatched) {
      setValue(
        "available_for_assign_role_ids",
        currentOrgId == organizationId ? defaultValues.available_for_assign_role_ids : []
      );
    }
  };

  const unregisterFields = () => {
    if (!isMultiOrg) {
      unregister("organization_ids", { keepDefaultValue: true });
    }

    if (isMultiOrg || !canAssignRoleWatched) {
      unregister("available_for_assign_role_ids", { keepDefaultValue: true });
    }

    if (isMultiOrg || isCorp || !isCorporateReporting) {
      unregister("user_group_user_ids", { keepDefaultValue: true });
    }

    if (isMultiOrg || isCorp) {
      unregister("clubready_store_ids", { keepDefaultValue: true });
    }

    if (!isCorporateReporting) {
      unregister("corporate_report_ids", { keepDefaultValue: true });
    }

    if (!isMultiOrg && !isCorp) {
      unregister("send_inactive_users_email", { keepDefaultValue: true });
    }
  };

  const filterRolesList = () => {
    if (isMultiOrg && currentOrgs) {
      let orgs = currentOrgs.map((org) => org.id);
      setOrgRoles(roles.filter((role) => orgs.includes(role.organizationId)));

      if (
        getValues("role_id") &&
        !currentOrgs.some((org) => org.id == getValues("role_id").organizationId)
      ) {
        setValue("role_id", null);
      }

      return;
    }

    let orgRoles = roles.filter((role) => role.organizationId === currentOrgId);
    setOrgRoles(orgRoles);

    if (!isMultiOrg && currentOrgId) {
      setValue("role_id", currentOrgId == organizationId ? selectedRole : orgRoles[0]);
    }
  };

  useEffect(() => {
    if (!isAdmin && currentUserInfo) {
      unregisterFields();
      filterRolesList();
      setAssignRoles();
      setLocations();
      setCorporateReports();
    }
  }, [
    currentUserInfo,
    isMultiOrg,
    canAssignRoleWatched,
    isCorp,
    isCorporateReporting,
    currentOrgs,
    currentOrgId
  ]);

  const onSubmit = (data) => {
    let changedFieldKeys = Object.keys(dirtyFields);
    let formattedDataForRequest = {};

    changedFieldKeys.forEach((key) => {
      if (
        key === "clubready_store_ids" ||
        key == "permission_ids" ||
        key == "corporate_report_ids"
      ) {
        formattedDataForRequest = {
          ...formattedDataForRequest,
          [key]: Object.keys(data[key]).map((id) => parseInt(id))
        };
      } else if (key === "role_id" || key === "organization_id") {
        formattedDataForRequest = {
          ...formattedDataForRequest,
          [key]: data[key].id.toString()
        };
      } else if (
        key === "organization_ids" ||
        key === "available_for_assign_role_ids" ||
        key === "user_group_user_ids"
      ) {
        formattedDataForRequest = {
          ...formattedDataForRequest,
          [key]: data[key].map((el) => el.id)
        };
      } else {
        formattedDataForRequest = { ...formattedDataForRequest, [key]: data[key] };
      }
    });

    console.log(formattedDataForRequest);

    dispatch(changeUser({ data: formattedDataForRequest, id, orgName, organizationId }));
  };

  if (!currentUserInfo) return {};

  return {
    form,
    submit: handleSubmit(onSubmit),
    linkUsersToSharePermissions,
    selectedRole,
    defaultValues,
    currentUserInfo: {
      ...currentUserInfo,
      userActions: {
        ...currentUserInfo.userActions,
        canUpdateLocations,
        canSendInactiveUsersEmail,
        canUpdateCorporateReports: isCorporateReporting
      }
    },
    watchedValues: {
      isMultiOrg,
      canAssignRole: canAssignRoleWatched,
      isCorporate: isCorp,
      isCorporateReporting
    },
    orgRoles,
    allPermissions,
    organizations
  };
};
